import { css } from "styled-components";
import { fonts } from "./fonts";
import { breakpoints } from "./breakpoints";

/**
 * Typographic roles: one mixin = face + fluid size + leading + tracking. A text element takes one role
 * (`${({ theme }) => theme.type.h2}`) instead of its own font-size/family/letter-spacing lines; case, colour and
 * spacing stay with the element. Sizes are the graphic designer's mocks (360 mobile ×1.167, 1920 desktop ×0.75);
 * the homepage roles follow the 24.09 mock read literally (1920 px = size from 1440 up, the content column is a fixed 1100).
 */

const ROOT = 16;
const rem = (px: number) => `${+(px / ROOT).toFixed(4)}rem`;

/** minPx at `breakpoints.typeMin`, maxPx at `breakpoints.typeMax`, linear in between; rem so it follows the user's font size */
const fluid = (minPx: number, maxPx: number) => {
  const slope = (maxPx - minPx) / (breakpoints.typeMax - breakpoints.typeMin);
  const intercept = minPx - slope * breakpoints.typeMin;
  return `clamp(${rem(minPx)}, ${rem(intercept)} + ${+(slope * 100).toFixed(4)}vw, ${rem(maxPx)})`;
};

/** the icon tile title's size, exported so the tile can cap it by its own width (IconTiles) */
export const tileHeadingSize = fluid(28, 36);

/* the licensed faces (fonts.css) — every role picks one, nothing else sets font-family */
const face = {
  serif: css`
    font-family: ${fonts.font.heading};
    font-weight: ${fonts.fontWeight.normal};
  `,
  serifItalic: css`
    font-family: ${fonts.font.heading};
    font-weight: ${fonts.fontWeight.normal};
    font-style: italic;
  `,
  condensedLight: css`
    font-family: ${fonts.font.display};
    font-weight: ${fonts.fontWeight.thin};
  `,
  condensed: css`
    font-family: ${fonts.font.display};
    font-weight: ${fonts.fontWeight.normal};
  `,
  sans: css`
    font-family: ${fonts.font.primary};
    font-weight: ${fonts.fontWeight.normal};
  `,
  sansItalic: css`
    font-family: ${fonts.font.primary};
    font-weight: ${fonts.fontWeight.normal};
    font-style: italic;
  `,
  sansBold: css`
    font-family: ${fonts.font.primary};
    font-weight: ${fonts.fontWeight.extraBold};
  `,
  sansThin: css`
    font-family: ${fonts.font.primary};
    font-weight: ${fonts.fontWeight.thin};
  `,
};

export const leading = {
  body: 1.35,
};

export const type = {
  /* page / section titles — New Spirit Light Condensed */
  display: css`
    ${face.condensedLight}
    font-size: ${fluid(26, 66)};
    line-height: 1.1;
    letter-spacing: -0.02em;
  `,
  h1: css`
    ${face.condensedLight}
    font-size: ${fluid(26, 52)};
    line-height: 1.15;
    letter-spacing: -0.02em;
  `,
  h2: css`
    ${face.condensedLight}
    font-size: ${fluid(21, 42)};
    line-height: 1.2;
    letter-spacing: -0.01em;
  `,
  /* homepage mock (360 and 1920 read literally) — a section is heading → italic subtitle → body */
  /* full-width band title ("Nie wiesz, jaki kosmetyk dobrać"); the emphasised line is an <em> in Regular Italic */
  bandTitle: css`
    ${face.serif}
    font-size: ${fluid(22, 60)};
    line-height: 1.1;
    letter-spacing: 0;
  `,
  sectionHeading: css`
    ${face.serif}
    font-size: ${fluid(22, 50)};
    line-height: 1.1;
    letter-spacing: 0;
  `,
  sectionSubtitle: css`
    ${face.serifItalic}
    font-size: ${fluid(18, 40)};
    line-height: 1.2;
    letter-spacing: 0;
  `,
  /* the quieter italic subtitle under a section heading (dopasuj "dodaj, aby osiągnąć…": 14 on a phone, 30 at 1920) */
  sectionSubtitleSmall: css`
    ${face.serifItalic}
    font-size: ${fluid(14, 30)};
    line-height: 1.3;
    letter-spacing: 0;
  `,
  /* section title in a beige panel (loyalty page, 25.09 mock): Regular Condensed 50; the phone keeps h1 */
  panelTitle: css`
    ${face.condensed}
    font-size: ${fluid(22, 50)};
    line-height: 1.1;
    letter-spacing: 0;
  `,
  /* title of the loyalty box — designer's spec 25.09: Light Condensed 40/50 (20 on a phone) */
  boxTitle: css`
    ${face.condensedLight}
    font-size: ${fluid(20, 40)};
    line-height: 1.25;
    letter-spacing: 0;
  `,
  /* title of the newsletter / search boxes — designer's spec 25.09: Light Condensed 28 (20 on a phone) */
  boxTitleSmall: css`
    ${face.condensedLight}
    font-size: ${fluid(20, 28)};
    line-height: 1.2;
    letter-spacing: 0;
  `,
  /* line under a boxTitleSmall — designer's spec 25.09: Lato Italic 20 */
  boxSubtitle: css`
    ${face.sansItalic}
    font-size: ${fluid(14, 20)};
    line-height: 1.3;
    letter-spacing: 0;
  `,
  /* blog / teaser card title (mobile 16 — a step under productNameTile's 20); 24 like the blog listing, not the mock's 32 — real titles run 3 lines at 32 */
  cardTitle: css`
    ${face.serif}
    font-size: ${fluid(16, 24)};
    line-height: 1.2;
    letter-spacing: 0;
  `,
  /* title of a feature card / tile (manufaktura 2×2): the 24.09 mock's 14 on a phone, 30 at 1920 — a step over cardTitle on desktop */
  featureTitle: css`
    ${face.serif}
    font-size: ${fluid(14, 30)};
    line-height: 1.2;
    letter-spacing: 0;
  `,
  /* one-line label over a card title ("naturalne porady"); colour from the element */
  kicker: css`
    ${face.sans}
    font-size: ${fluid(13, 21)};
    line-height: 1.3;
    letter-spacing: 0;
  `,
  /* dopasuj: label on the beige band over a group of tags (25.09 mock: Light Condensed 30 at 1920; phone 18) */
  groupTitle: css`
    ${face.condensedLight}
    font-size: ${fluid(18, 30)};
    line-height: 1.2;
    letter-spacing: 0;
  `,
  /* dopasuj: the "+" in the round add button of a set card (25.09 mock: Lato 14 on a phone, 29 at 1920; the circle is 1.3em) */
  cardAdd: css`
    ${face.sans}
    font-size: ${fluid(14, 29)};
    line-height: 1;
    letter-spacing: 0;
  `,
  /* dopasuj set card (30.09 mock): product name NS 18 → 29, subtitle Lato Italic 12 → 18, two price lines Lato 11/14 → 16/24 */
  setCardName: css`
    ${face.serif}
    font-size: ${fluid(18, 29)};
    line-height: 1.2;
    letter-spacing: 0;
  `,
  setCardSub: css`
    ${face.sansItalic}
    font-size: ${fluid(12, 18)};
    line-height: 1.2;
    letter-spacing: 0;
  `,
  /* category page: subcategory pill under the hero (30.09 mock: Light Condensed 18 on a phone, 22 at 1920) */
  subcategoryPill: css`
    ${face.condensedLight}
    font-size: ${fluid(18, 22)};
    line-height: 1.2;
    letter-spacing: 0;
  `,
  /* dopasuj: hashtag pill (25.09 mock: Lato 22 at 1920, 14 on a phone) */
  tagPill: css`
    ${face.sans}
    font-size: ${fluid(14, 22)};
    line-height: 1.2;
    letter-spacing: 0;
  `,
  /* italic lead under a section subtitle (product of the month) */
  leadItalic: css`
    ${face.sansItalic}
    font-size: ${fluid(14, 18)};
    line-height: ${leading.body};
    letter-spacing: 0;
  `,
  /* large text field (homepage search, newsletter): the mock's 23 at 1920; 16 on a phone, under it iOS Safari zooms on focus */
  field: css`
    ${face.sansItalic}
    font-size: ${fluid(16, 23)};
    line-height: 1.3;
    letter-spacing: 0;
  `,
  /* typed value of a form field: body size, so the narrow fields (flat, postcode) keep their digits in view */
  input: css`
    ${face.sans}
    font-size: ${fluid(14, 16.5)};
    line-height: 1.3;
    letter-spacing: 0;
  `,
  /* body-sized italic: card excerpts, input placeholders */
  bodyItalic: css`
    ${face.sansItalic}
    font-size: ${fluid(14, 16.5)};
    line-height: ${leading.body};
    letter-spacing: 0;
  `,
  /* title of a system page (login gate, cart, checkout, account): italic, 26 on a phone → 50 at 1440 */
  pageTitle: css`
    ${face.serifItalic}
    font-size: ${fluid(26, 50)};
    line-height: 1;
    letter-spacing: 0;
  `,
  /* category hero description — the XD mobile mock's 16 read literally; desktop end = body */
  categoryDescription: css`
    ${face.sans}
    font-size: ${fluid(16, 16.5)};
    line-height: ${leading.body};
    letter-spacing: 0;
  `,
  /* step / tier titles, sub-headings — New Spirit Regular */
  h3: css`
    ${face.serif}
    font-size: ${fluid(20.5, 34)};
    line-height: 1.17;
    letter-spacing: 0;
  `,
  /* product copy sub-heading (PDP accordion, `##` in product texts) — XD mobile 14/18, desktop 24/30, read literally */
  subheading: css`
    ${face.serif}
    font-size: ${fluid(14, 24)};
    line-height: 1.25;
    letter-spacing: 0;
  `,
  h4: css`
    ${face.serif}
    font-size: ${fluid(15, 20)};
    line-height: 1.2;
    letter-spacing: 0;
  `,
  /* step title / level label on the loyalty page (25.09 mock 28); 20.5 at 360 = the h3 those elements had */
  tileTitle: css`
    ${face.serif}
    font-size: ${fluid(20.5, 28)};
    line-height: 1.15;
    letter-spacing: 0;
  `,
  productName: css`
    ${face.serif}
    font-size: ${fluid(26, 34)};
    line-height: 1.2;
    letter-spacing: 0;
  `,
  productNameTile: css`
    ${face.serif}
    font-size: ${fluid(20, 24)};
    line-height: 1.2;
    letter-spacing: 0;
  `,
  productSub: css`
    ${face.sans}
    font-size: ${fluid(12, 14)};
    line-height: 1.3;
    letter-spacing: -0.02em;
  `,
  price: css`
    ${face.serif}
    font-size: ${fluid(17, 21)};
    line-height: 1;
    letter-spacing: 0;
  `,
  /* the message in the promo bar over the navbar */
  promoBar: css`
    ${face.serif}
    font-size: ${fluid(14, 18)};
    line-height: 1.2;
    letter-spacing: 0;
  `,
  priceSmall: css`
    ${face.serif}
    font-size: ${fluid(12, 15)};
    line-height: 1;
    letter-spacing: 0;
  `,
  /* price on a cart line */
  linePrice: css`
    ${face.serif}
    font-size: ${fluid(13, 18)};
    line-height: 1.2;
    letter-spacing: 0;
  `,
  /* one size: pageTitle grows to 50 and would not fit a sheet that keeps its width */
  sheetTitle: css`
    ${face.serifItalic}
    font-size: ${fluid(22, 24)};
    line-height: 1.1;
    letter-spacing: 0;
  `,
  /* the title of an icon tile (about page "prosto / naturalnie / skutecznie", 23.10 mock: 28 / 36) */
  tileHeading: css`
    ${face.serifItalic}
    font-size: ${tileHeadingSize};
    line-height: 1.1;
    letter-spacing: 0;
  `,
  /* a pull quote over a band (about page, 23.10 mock: 20 / 24); looser than sheetTitle, it runs to four lines */
  quote: css`
    ${face.serifItalic}
    font-size: ${fluid(20, 24)};
    line-height: 1.25;
    letter-spacing: 0;
  `,
  rowName: css`
    ${face.serif}
    font-size: ${fluid(15, 16)};
    line-height: 1.25;
    letter-spacing: 0;
  `,
  /* a row of the order summary: products, discounts, delivery */
  summaryFee: css`
    ${face.serif}
    font-size: ${fluid(15, 16)};
    line-height: 1.3;
    letter-spacing: 0;
  `,
  numberBig: css`
    ${face.serif}
    font-size: ${fluid(30, 49)};
    line-height: 1;
    letter-spacing: -0.01em;
  `,
  numberSmall: css`
    ${face.serif}
    font-size: ${fluid(19, 40)};
    line-height: 1;
    letter-spacing: -0.01em;
  `,
  /* running text — Lato */
  body: css`
    ${face.sans}
    font-size: ${fluid(14, 16.5)};
    line-height: ${leading.body};
    letter-spacing: 0;
  `,
  /* the INCI list on the PDP: Lato 300 at body size, so the Latin names read lighter than the copy around them */
  bodyLight: css`
    ${face.sansThin}
    font-size: ${fluid(14, 16.5)};
    line-height: ${leading.body};
    letter-spacing: 0;
  `,
  lead: css`
    ${face.sansBold}
    font-size: ${fluid(14, 27)};
    line-height: 1.3;
    letter-spacing: 0;
  `,
  /* body-sized emphasis: row / card / caption titles outside the PDP (cart rows, footer panels, landings) */
  bodyBold: css`
    ${face.sansBold}
    font-size: ${fluid(14, 16.5)};
    line-height: ${leading.body};
    letter-spacing: 0;
  `,
  small: css`
    ${face.sans}
    font-size: ${fluid(12.5, 14)};
    line-height: 1.3;
    letter-spacing: 0;
  `,
  /* the summary card's reassurance lines: stay below the fee rows (summaryFee) on every width */
  summaryNote: css`
    ${face.sans}
    font-size: ${fluid(14, 15)};
    line-height: 1.3;
    letter-spacing: 0;
  `,
  /* menu / footer links; row spacing is the list's job, not line-height */
  nav: css`
    ${face.sans}
    font-size: ${fluid(11.2, 14)};
    line-height: ${leading.body};
    letter-spacing: 0;
  `,
  /* small caps label (footer column headings, kickers, list labels) — Lato Bold with the mockup's tight −0.07em tracking,
     which only works in caps, so the role carries the uppercase */
  navLabel: css`
    ${face.sansBold}
    font-size: ${fluid(12.7, 14)};
    line-height: 1.2;
    letter-spacing: -0.07em;
    text-transform: uppercase;
  `,
  badge: css`
    ${face.sansBold}
    font-size: ${fluid(12.5, 20)};
    line-height: 1;
    letter-spacing: 0;
  `,
  /* homepage / listing section headers (`TitleHomepage`): the pre-New-Spirit Lato pair, kept until the designer settles the
     section titles — thin wide-tracked kicker over a light caps title; the element sets the uppercase */
  sectionKicker: css`
    ${face.sansThin}
    font-size: ${fluid(14, 16)};
    line-height: 1.3;
    letter-spacing: 0.3em;
  `,
  sectionTitle: css`
    ${face.sans}
    font-size: ${fluid(22.4, 25.6)};
    line-height: 1.2;
    letter-spacing: 0.12em;
  `,
  /* CTA — New Spirit Light Condensed: every button in the homepage mock (28 at 1920 → 21, 16 at 360); site-wide */
  button: css`
    ${face.condensedLight}
    font-size: ${fluid(16, 21)};
    line-height: 1;
    letter-spacing: 0;
  `,
  /* homepage section CTA — same face, the 24.09 mock's 28 at 1920; PDP / cart / checkout keep "button" */
  cta: css`
    ${face.condensedLight}
    font-size: ${fluid(16, 28)};
    line-height: 1;
    letter-spacing: 0;
  `,
  /* the one bigger CTA on a full-width photo banner (tattoo) — designer's spec 25.09: 35 */
  ctaLarge: css`
    ${face.condensedLight}
    font-size: ${fluid(16, 35)};
    line-height: 1;
    letter-spacing: 0;
  `,
  /* FAQ questions (27.10 mock): Lato bold, under the serif group bands on every width */
  faq: css`
    ${face.sansBold}
    font-size: ${fluid(15, 18)};
    line-height: 1.35;
    letter-spacing: 0;
  `,
  /* footer / accordion headings — New Spirit Regular Condensed */
  navHeading: css`
    ${face.condensed}
    font-size: ${fluid(21, 24)};
    line-height: 1.2;
    letter-spacing: 0;
  `,
};
